import { useMemo } from 'react';

import { useGlobalRegions } from '../../../hooks';
import { GlobalRegion, UNPRICED_EXTRAS } from '../../../shared/constants';
import { useTranslationContext } from '../../../shared/context';
import { AncillariesI18nKeys } from '../../../shared/types';
import {
  bookingFlowStoreSelector,
  preCheckStoreSelector,
} from '../../../store';
import { PricingSummaryData } from '../../AncillariesBundles/AncillariesBundle';

/**
 * Minimal structural shape of the AEM add-ons config needed to resolve a
 * display name from an add-on code. Declared locally to avoid a molecule ->
 * organism import (layering) while staying compatible with `AemAddOnsConfig`.
 */
type AddOnsConfigLike = {
  data?: {
    addOnCategoryList?: {
      items?: Array<{
        addOnList?: Array<{
          code?: string;
          name?: string;
          subAddOnList?: Array<{ code?: string; name?: string }>;
        }>;
      }>;
    };
  };
};

/**
 * Build a `code -> name` lookup from the AEM add-ons config so unpriced (TBC)
 * extras can be listed by their human-readable names in the notification.
 */
const buildAddOnNameLookup = (
  config?: AddOnsConfigLike | null,
): Record<string, string> => {
  const lookup: Record<string, string> = {};
  const categories = config?.data?.addOnCategoryList?.items ?? [];

  for (const category of categories) {
    for (const addOnItem of category.addOnList ?? []) {
      if (addOnItem.code && addOnItem.name) {
        lookup[addOnItem.code] = addOnItem.name;
      }

      for (const subItem of addOnItem.subAddOnList ?? []) {
        if (subItem.code && subItem.name) {
          lookup[subItem.code] = subItem.name;
        }
      }
    }
  }

  return lookup;
};

export const useUnpricedExtrasNotes = (
  pricingData?: PricingSummaryData | null,
) => {
  const { t, i18nKeys } = useTranslationContext<AncillariesI18nKeys>();
  const { belongsToRegions } = useGlobalRegions();
  const isEmea = belongsToRegions([GlobalRegion.EMEA]);
  const addOnsData = bookingFlowStoreSelector.use.addOnsData();
  const addOnsConfig = bookingFlowStoreSelector.use.addOnsConfig();

  const isAvisFirst =
    bookingFlowStoreSelector.use.isAvisFirst() ||
    preCheckStoreSelector.use.preCheckInfo()?.reservationInformation
      ?.brandInformation?.subBrand === 'Q';

  const unpricedExtrasNotes = useMemo(() => {
    // Collect the user-selected add-ons that can't be priced yet (TBC):
    // either a known unpriced code (e.g. GSO/FUE) or flagged priceOnRequest.
    const unpricedExtrasCodes =
      pricingData?.addOnItems
        ?.filter((item) => {
          if (UNPRICED_EXTRAS.includes(item?.code)) return true;
          // Check extras store for generic priceOnRequest flag
          const extrasItem = addOnsData?.addOnItems?.find(
            (extension) => extension.code === item?.code,
          );
          return !!extrasItem?.priceOnRequest;
        })
        .map((item) => item.code) ?? [];

    if (unpricedExtrasCodes.length === 0 || !isEmea) {
      return '';
    }

    // Resolve readable names (fall back to the code) and de-duplicate.
    const nameLookup = buildAddOnNameLookup(addOnsConfig);
    const addon = [
      ...new Set(unpricedExtrasCodes.map((code) => nameLookup[code] || code)),
    ].join(', ');

    if (isAvisFirst) {
      return t(i18nKeys.unpricedExtrasAvisFirstNotification, { addon });
    }

    return t(i18nKeys.unpricedExtrasNotification, { addon });
  }, [
    pricingData?.addOnItems,
    addOnsData?.addOnItems,
    addOnsConfig,
    isEmea,
    i18nKeys,
    isAvisFirst,
    t,
  ]);

  return unpricedExtrasNotes;
};
